import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, InputRef, Skeleton, Spin } from 'antd';
import { BankOutlined, EnterOutlined, PlusOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useIntl } from 'umi';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import { useContactDirectory } from '@/components/Contacts/useContactDirectory';
import { ContactModel, CUSTOMER_TYPES } from '@/typings/models/Contact';
import { CONTACT_ENTITY, getContactCustomers, getContactName, getCustomerLabel } from '@/utils/contacts';
import { F, getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import styles from './index.module.less';

const Highlight: React.FC<{ text: string; term: string }> = ({ text, term }) => {
  const query = term.trim();
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.highlight}>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
};

/** How close to the end of the list (px) the next page of the directory loads. */
const LOAD_MORE_THRESHOLD = 48;

/** Where the contact is linked today: the first customer, the rest as "+n". */
const CustomerBadge: React.FC<{ contact: ContactModel }> = ({ contact }) => {
  const [first, ...rest] = getContactCustomers(contact);
  if (!first) return null;
  return (
    <span
      className={styles.searchResultCustomer}
      title={rest.length ? [first, ...rest].map(getCustomerLabel).join(', ') : undefined}
    >
      {first.type === CUSTOMER_TYPES.ORGANIZATION ? <BankOutlined /> : <UserOutlined />}
      {getCustomerLabel(first)}
      {rest.length > 0 && <span className={styles.searchResultCustomerMore}>+{rest.length}</span>}
    </span>
  );
};

type ContactSearchPanelProps = {
  /** The project's customer: its contacts are on the project already, so they aren't offered. */
  customerId?: string;
  term: string;
  onTermChange: (term: string) => void;
  /** A contact of the partner, to link to this customer (and so to the project). */
  onPick: (contact: ContactModel) => void;
  onCreate: (term: string) => void;
  onCancel: () => void;
};

/**
 * The "+" search: one compact list of the partner's contacts that aren't this
 * customer's yet, each with where it's linked today, loading more on scroll.
 * Typing filters it; Enter picks the highlighted row, and the last stop
 * creates a new contact from what was typed.
 */
const ContactSearchPanel: React.FC<ContactSearchPanelProps> = ({
  customerId,
  term,
  onTermChange,
  onPick,
  onCreate,
  onCancel,
}) => {
  const intl = useIntl();
  const inputRef = useRef<InputRef>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const directory = useContactDirectory(term, customerId);
  const results = directory.contacts;
  // The last keyboard stop is "create new contact".
  const stops = results.length + 1;

  useEffect(() => setActiveIndex(0), [term]);
  useEffect(() => inputRef.current?.focus(), []);
  // The keyboard's row stays in view, and reaching the end loads the next page.
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    if (activeIndex >= results.length - 2) directory.loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const onListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight < LOAD_MORE_THRESHOLD) directory.loadMore();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % stops);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + stops) % stops);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const contact = results[activeIndex];
      if (contact) onPick(contact);
      else onCreate(term);
    } else if (event.key === 'Escape') {
      // Only the search closes, not the project drawer around it.
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  const loading = directory.loading && !results.length;
  const createActive = activeIndex === results.length;

  return (
    <div className={styles.panel}>
      <Input
        ref={inputRef}
        value={term}
        onChange={(event) => onTermChange(event.target.value)}
        onKeyDown={onKeyDown}
        prefix={<SearchOutlined />}
        allowClear
        placeholder={intl.formatMessage({
          id: 'component.contacts.searchPlaceholder',
          defaultMessage: 'Name, email, phone',
        })}
        aria-label={getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.SEARCH })}
      />

      {loading ? (
        <Skeleton active avatar={{ shape: 'square', size: 'small' }} paragraph={{ rows: 1 }} title={false} />
      ) : (
        results.length > 0 && (
          <div className={styles.searchResults} role="listbox" ref={listRef} onScroll={onListScroll}>
            {results.map((contact, index) => {
              const detail = contact.email || contact.phone;
              return (
                <button
                  type="button"
                  key={contact.id}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={classNames(styles.searchResult, {
                    [styles.searchResultActive]: index === activeIndex,
                  })}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => onPick(contact)}
                >
                  <ContactAvatar contact={contact} size="small" />
                  <span className={styles.searchResultName}>
                    <Highlight text={getContactName(contact)} term={term} />
                  </span>
                  {detail && (
                    <span className={styles.searchResultDetails}>
                      <Highlight text={detail} term={term} />
                    </span>
                  )}
                  <CustomerBadge contact={contact} />
                  {index === activeIndex && <EnterOutlined className={styles.searchResultEnter} />}
                </button>
              );
            })}
            {directory.loading && (
              <div className={styles.searchResultsMore}>
                <Spin size="small" />
              </div>
            )}
          </div>
        )
      )}

      <div className={styles.panelFooter}>
        <button
          type="button"
          className={classNames(styles.createButton, { [styles.createButtonActive]: createActive })}
          onMouseEnter={() => setActiveIndex(results.length)}
          onClick={() => onCreate(term)}
        >
          <PlusOutlined />
          {term.trim() ? (
            <F
              id="component.contacts.createFromSearch"
              defaultMessage="Create new contact “{term}”"
              values={{ term: term.trim() }}
            />
          ) : (
            <F entity={CONTACT_ENTITY} action={INTL_ACTION.NEW} />
          )}
        </button>
        <Button size="small" onClick={onCancel}>
          <F id="common.actions.cancel" defaultMessage="Cancel" />
        </Button>
      </div>
    </div>
  );
};

export default ContactSearchPanel;
